<template>
  <v-card
    flat
    :height="height"
    :min-width="isMobile ? 'auto' : '500'"
    class="chronology-list"
  >
    <div class="d-flex align-center mb-3 pt-2">
      <v-btn small @click="showEditorDialog()" class="px-2">
        新規登録
      </v-btn>
      <div class="d-flex justify-end flex-grow-1 align-center">
        <v-btn small @click="excludeCompleted = !excludeCompleted" class="px-2">
          <v-icon small :color="excludeCompleted ? 'primary' : 'grey'">
            mdi-circle
          </v-icon>
          <span class="pl-1" :class="{ 'grey--text': !excludeCompleted }">
            完了を省く
          </span>
        </v-btn>
        <v-btn small @click="autoLoad = !autoLoad" class="ml-2 px-2">
          <v-icon small :color="autoLoad ? 'primary' : 'grey'">
            mdi-circle
          </v-icon>
          <span class="pl-1" :class="{ 'grey--text': !autoLoad }">
            自動更新
          </span>
        </v-btn>
        <v-btn
          small
          @click="isMiniMode = !isMiniMode"
          v-if="!isMobile"
          class="ml-2 px-2"
        >
          <v-icon small :color="isMiniMode ? 'primary' : 'grey'">
            mdi-circle
          </v-icon>
          <span class="pl-1" :class="{ 'grey--text': !isMiniMode }">
            簡易表示
          </span>
        </v-btn>
        <ChronologyNotification />
      </div>
    </div>
    <div class="d-flex align-center mb-1">
      <template v-if="!isMobile">
        <v-btn small @click="updateLatest()" class="mr-2">
          <v-icon small color="#757575">mdi-refresh</v-icon>
        </v-btn>
        <v-btn small @click="downloadCsv()" class="mr-2">
          <v-icon small color="#757575">mdi-download</v-icon>
        </v-btn>
      </template>
      <v-card class="d-flex flex-wrap justify-end flex-grow-1" flat>
        <v-select
          :items="users"
          style="font-size: 12px; max-width: 120px"
          class="mr-1 mb-1"
          v-model="searchFilter.user"
          dense
          hide-details
          outlined
          @change="filterRow()"
        />
        <v-select
          :items="categories"
          style="font-size: 12px; max-width: 140px"
          class="mr-0 mb-1"
          v-model="searchFilter.category"
          dense
          hide-details
          outlined
          @change="fetchChronology()"
        />
        <v-text-field
          v-if="!isMiniMode"
          placeholder="内容・対応状況"
          style="font-size: 12px; max-width: 264px"
          class="ml-1"
          v-model="searchFilter.content"
          dense
          hide-details
          outlined
          clearable
          @input="filterRow()"
        />
      </v-card>
    </div>
    <v-simple-table fixed-header :height="tableHeight">
      <thead>
        <tr>
          <th class="text-center" style="min-width: 38px"></th>
          <th class="text-center" style="min-width: 38px">
            ID
          </th>
          <th class="text-left" style="min-width: 110px">
            <div>カテゴリ</div>
            <div>情報種別</div>
          </th>
          <th class="text-left" v-if="!isMiniMode" style="min-width: 100px">
            件名
          </th>
          <th class="text-left" v-if="!isMiniMode" style="min-width: 180px">
            対応状況
          </th>
          <th class="text-left" style="min-width: 110px">
            <div>日時</div>
            <div>入力者</div>
          </th>
          <th class="text-center" style="min-width: 53px">
            <div>報告</div>
            <div>履歴</div>
          </th>
          <th class="text-center" style="min-width: 53px">
            <div>状況</div>
            <div>報告</div>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(row, i) in rows"
          :key="i"
          @click="showOnMap(row)"
          :style="{
            backgroundColor: row.id === selectedId ? '#FFF8E1' : ''
          }"
          :id="`row-${row.id}`"
        >
          <td class="text-center" :class="getSelectedClass(row)">
            <v-icon :color="row.color">{{ row.icon }}</v-icon>
          </td>
          <td>
            <div style="cursor: pointer" @click.stop="showEditorDialog(row)">
              <div class="blue--text text--darken-4">{{ row.id }}</div>
              <div class="grey--text text--darken-3">{{ row.status }}</div>
            </div>
          </td>
          <td>
            <div>{{ row.category }}</div>
            <div>{{ row.section }}</div>
          </td>
          <td v-if="!isMiniMode">
            <template v-if="row.notes.length > 50">
              <v-tooltip left max-width="200">
                <template v-slot:activator="{ on, attrs }">
                  <div class="long-text" v-bind="attrs" v-on="on">
                    {{ row.notes }}
                  </div>
                </template>
                <span>{{ row.notes }}</span>
              </v-tooltip>
            </template>
            <template v-else>
              <div class="long-text">{{ row.notes }}</div>
            </template>
          </td>
          <td v-if="!isMiniMode">
            <div class="d-flex" v-if="row.progress">
              <v-card
                v-if="row.progress.url"
                width="80"
                height="56"
                class="mr-2 text-center"
                color="transparent"
                :flat="row.progress.isPdf"
                @click="previewProgressFile(row.progress.url)"
              >
                <v-icon v-if="row.progress.isPdf" x-large class="mt-2">
                  mdi-file-pdf-box
                </v-icon>
                <v-img v-else width="80" height="56" :src="row.progress.url" />
              </v-card>
              <div v-if="row.progress.text">
                <template v-if="row.progress.text.length > 50">
                  <v-tooltip left max-width="200">
                    <template v-slot:activator="{ on, attrs }">
                      <div class="long-text" v-bind="attrs" v-on="on">
                        {{ row.progress.text }}
                      </div>
                    </template>
                    <span>{{ row.progress.text }}</span>
                  </v-tooltip>
                </template>
                <template v-else>
                  <div class="long-text">{{ row.progress.text }}</div>
                </template>
              </div>
            </div>
          </td>
          <td>
            <div>
              {{ row.jst }}
            </div>
            <div>
              {{ row.displayUser }}
            </div>
          </td>
          <td class="text-center">
            <v-btn
              x-small
              @click.stop="showHistory(row)"
              class="mt-2"
              :disabled="!row.progress || row.progress.count === 0"
            >
              <v-badge
                color="#FFB74D"
                :content="
                  row.progress && row.progress.count ? row.progress.count : 0
                "
                :value="row.progress && row.progress.count ? true : false"
              >
                履歴
              </v-badge>
            </v-btn>
          </td>
          <td>
            <v-btn x-small @click.stop="appendProcess(row)" class="mt-2">
              追加
            </v-btn>
          </td>
        </tr>
      </tbody>
    </v-simple-table>
    <ChronologyEditor
      :dialog="editorDialog"
      :data="targetRow"
      @close="closeDialog"
      @closeAndProgress="closeEditorAndProgress"
    />
    <ChronologyDetail
      :dialog="detailDialog"
      :data="detailRow"
      @close="closeDialog"
    />
    <ChronologyProgress
      :dialog="progressDialog"
      :data="progressRow"
      @close="closeDialog"
    />
    <ChronologyRegister :dialog="registerDialog" @close="closeDialog" />
  </v-card>
</template>
<script>
import moment from "moment";
import { Chronology } from "../../enums/Chronology";
import chronologyAPI from "../../utils/chronologyAPI";
import ChronologyRegister from "./ChronologyRegister";
import ChronologyEditor from "./ChronologyEditor";
import ChronologyDetail from "./ChronologyDetail";
import ChronologyProgress from "./ChronologyProgress";
import ChronologyNotification from "./ChronologyNotification";
export default {
  name: "ChronologyList",
  components: {
    ChronologyRegister,
    ChronologyEditor,
    ChronologyDetail,
    ChronologyProgress,
    ChronologyNotification
  },

  props: {
    map: {
      type: Object
    },
    height: {
      type: [String, Number],
      default: "calc(100dvh - 40px)"
    }
  },

  data() {
    return {
      Chronology,
      baseRows: [],
      rows: [],
      searchFilter: {
        category: "カテゴリ",
        user: "入力者",
        content: ""
      },
      targetRow: null,
      detailRow: null,
      progressRow: null,
      editorDialog: false,
      registerDialog: false,
      detailDialog: false,
      progressDialog: false,
      autoLoad: true,
      isMiniMode: false,
      excludeCompleted: true,
      subscribeTimer: null
    };
  },

  computed: {
    baseDate() {
      return this.$store.getters.baseDate;
    },
    categories() {
      return ["カテゴリ", ...Chronology.categories];
    },
    isMobile() {
      return this.$route.path === "/mobile-chronology";
    },
    selectedId() {
      const row = this.$store.getters.selectedChronology;
      return row ? row.id : 0;
    },
    tableHeight() {
      return this.isMobile ? "calc(100dvh - 210px)" : "calc(100dvh - 120px)";
    },
    users() {
      const users = this.baseRows.map(row => row.displayUser);
      users.unshift("入力者");
      if (!users.includes(this.searchFilter.user)) {
        users.push(this.searchFilter.user);
      }
      return Array.from(new Set(users));
    }
  },

  watch: {
    baseDate(newval) {
      const now = moment.utc().format("YYYY/MM/DD HH:mm");
      const current = newval.format("YYYY/MM/DD HH:mm");
      if (now !== current) {
        this.autoLoad = false;
      } else {
        this.autoLoad = true;
      }
      this.fetchChronology();
    },
    autoLoad(value) {
      if (value) {
        this.fetchChronology();
        this.subscribe();
      } else {
        this.unsubscribe();
      }
    },
    isMiniMode(newval, oldval) {
      if (newval !== oldval) {
        if (this.map) {
          setTimeout(() => {
            this.map.invalidateSize();
          }, 300);
        }
      }
    },
    excludeCompleted(newval, oldval) {
      if (newval !== oldval) {
        this.fetchChronology();
      }
    },
    selectedId(val) {
      // リストを見える位置にスクロール
      const targetIndex = this.rows.findIndex(row => row.id === val);
      if (targetIndex < 0) return;
      const id = `#row-${this.rows[targetIndex].id}`;
      const height = this.$el.querySelector(id).clientHeight;
      const tableWrapper = this.$el.getElementsByClassName(
        "v-data-table__wrapper"
      )[0];
      tableWrapper.scroll({ top: height * targetIndex, behavior: "smooth" });
    }
  },

  created() {
    this.searchFilter.category = this.categories[0];
    document.addEventListener("visibilitychange", this.visibilityChange);
  },

  beforeDestroy() {
    this.unsubscribe();
    document.removeEventListener("visibilitychange", this.visibilityChange);
  },

  mounted() {
    this.fetchChronology();
    this.subscribe();
  },

  methods: {
    updateLatest() {
      this.$store.commit("SET_BASE_DATE", moment.utc());
    },

    visibilityChange() {
      if (document.visibilityState === "visible") {
        this.fetchChronology();
      }
    },

    showEditorDialog(row = null) {
      this.targetRow = row;
      if (row === null) {
        this.registerDialog = true;
      } else {
        this.editorDialog = true;
      }
    },

    getJst(date) {
      return moment
        .utc(date, "YYYYMMDDHHmm")
        .local()
        .format("YYYY/MM/DD HH:mm");
    },

    showS3Url(url) {
      window.open(url);
    },

    sortBaseRows() {
      this.baseRows.forEach(row => {
        if (row.progress && row.progress.date) {
          row.jst = moment
            .utc(row.progress.date, "YYYYMMDDHHmm")
            .local()
            .format("YYYY/MM/DD HH:mm");
        } else {
          row.jst = moment
            .utc(row.date, "YYYYMMDDHHmm")
            .local()
            .format("YYYY/MM/DD HH:mm");
        }
        if (row.progress && row.progress.s3Key) {
          row.progress.url = `https://hodatsushimizu-chronology.riskma.jp/${row.progress.s3Key}`;
          row.progress.isPdf = row.progress.s3Key.endsWith(".pdf");
        }
        row.displayUser =
          row.progress && row.progress.user ? row.progress.user : row.user;
      });
      return this.rows.sort((a, b) => {
        if (a.jst < b.jst) return 1;
        if (a.jst > b.jst) return -1;
        return 0;
      });
    },

    unsubscribe() {
      clearInterval(this.subscribeTimer);
    },

    async subscribe() {
      this.unsubscribe();
      this.subscribeTimer = setInterval(() => {
        this.updateLatest();
      }, 60 * 1000);
    },

    async fetchChronology() {
      const params = {
        date: this.baseDate.format("YYYYMMDDHHmm"),
        category:
          this.searchFilter.category === this.categories[0]
            ? ""
            : this.searchFilter.category,
        excludeCompleted: this.excludeCompleted
      };
      const response = await chronologyAPI.get("/chronology", params);
      if (response && response.messages) {
        this.baseRows = response.messages;
        this.sortBaseRows();
        this.filterRow();
      } else {
        this.baseRows = [];
        this.rows = [];
      }
      if (this.autoLoad) {
        this.subscribe();
      }
    },

    filterRow() {
      let rows = this.baseRows.filter(row => {
        if (this.searchFilter.user === this.users[0]) {
          return true;
        }
        return row.displayUser === this.searchFilter.user;
      });
      if (this.searchFilter.content) {
        rows = rows.filter(row => {
          return (
            row.notes.includes(this.searchFilter.content) ||
            (row.progress &&
              row.progress.text.includes(this.searchFilter.content))
          );
        });
      }

      this.rows = rows;
      this.$store.commit("SET_CHRONOLOGIES", [...this.rows]);
    },

    showOnMap(row) {
      this.$store.commit("SET_SELECTED_CHRONOLOGY", row);
      if (!this.map) return;
      if (row.lat && row.lng) {
        this.map.setView([row.lat, row.lng], 14);
      }
    },

    showHistory(row) {
      this.detailRow = row;
      this.detailDialog = true;
    },

    appendProcess(row, user = "") {
      this.progressRow = {
        hash: `${row.hash}-${row.id}`,
        id: 0,
        chronologyId: row.id,
        user: user
      };
      this.progressDialog = true;
    },

    getSelectedClass(row) {
      return row.id === this.selectedId ? "selected" : "unselected";
    },

    previewProgressFile(url) {
      window.open(url, "_blank");
    },

    refresh() {
      if (this.autoLoad) {
        this.updateLatest();
      } else {
        this.fetchChronology();
      }
    },

    closeDialog() {
      this.registerDialog = false;
      this.editorDialog = false;
      this.detailDialog = false;
      this.progressDialog = false;
      this.refresh();
    },

    closeEditorAndProgress(row) {
      this.editorDialog = false;
      this.appendProcess(row, row.user);
    },

    downloadCsv() {
      const getCsvItem = val => {
        return val
          .replaceAll("\r\n", "")
          .replaceAll("\n", "")
          .replaceAll(",", "、");
      };
      const filename =
        "chronology-" +
        this.baseDate
          .clone()
          .local()
          .format("YYYYMMDDHHmmss") +
        ".csv";
      let data =
        "ID,対応状況,カテゴリ,情報種別,内容,対応状況,日時,入力者,緯度,経度\r\n";
      this.rows.forEach(row => {
        const progress = getCsvItem(row.progress ? row.progress.text : "");
        const notes = getCsvItem(row.notes);
        data += `${row.id},${row.status},${row.category},${row.section},${notes},${progress},${row.jst},${row.displayUser},${row.lat},${row.lng}\r\n`;
      });

      const bom = new Uint8Array([0xef, 0xbb, 0xbf]);
      var elm = document.createElement("a");
      elm.download = filename;
      elm.href = URL.createObjectURL(
        new Blob([bom, data], { type: "text/csv" })
      );
      elm.dataset.downloadurl = ["text/csv", elm.download, elm.href].join(":");
      elm.click();
    }
  }
};
</script>

<style lang="scss" scoped>
th {
  padding: 3px 6px !important;
  font-size: 12px !important;
}
td {
  padding: 10px 6px !important;
  font-size: 12px !important;
}
th {
  background-color: #5c6bc0 !important;
  color: white !important;
}
td {
  &:not(:nth-child(1)) {
    vertical-align: top !important;
  }
}
tr {
  &:nth-child(2n) {
    background-color: #fcfcfc;
  }
}
.selected {
  border-left: 4px solid rgb(255, 121, 121);
}
.unselected {
  border-left: 4px solid rgb(220, 220, 220);
}
.long-text {
  font-family: monospace;
  max-height: 50px;
  overflow: hidden;
  color: #6f6f6f;
}
.v-tooltip__content {
  font-size: 12px !important;
  background-color: rgb(61, 61, 61);
}
</style>

<style lang="scss">
.mode-switch .v-label {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.87);
}
.chronology-list {
  .v-input__slot {
    min-height: 30px !important;
  }
  .v-select__selections {
    padding: 0 !important;
  }
  .v-input__append-inner {
    margin-top: 4px !important;
  }
}
</style>
